import React, { useEffect, useState } from "react";
import {
  ButtonComp,
  CardComp,
  MoneyInputComp,
  SelectComp2,
  TextInputComp,
} from "../../../../components";
import FilterFind from "../../../../components/FilterFind";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useCreateLoanTypeMutation,
  useGetDefaultLoanModelQuery,
} from "../../../../store/admin/loan";
import toast from "react-hot-toast";
import { useEnums } from "../../../../hooks/useEnums";
import {
  LOAN_MODEL_OPTIONS,
  WEEK_OF_MONTH_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  INTEREST_CALCULATION_MODE_OPTIONS,
} from "../../../../constants/loanModel";
import { interestModeOptions as buildInterestModeOptions } from "../../../../components/Loan/loanTypeModel";
import { PARTIAL_OVERRIDE_OPTIONS, overrideToPayload } from "../../../../components/Loan/partialRepayment";
import LateFeeFields from "../../../../components/Loan/LateFeeFields";
import { lateFeeInitialValues, lateFeeToPayload, lateFeeValidation } from "../../../../components/Loan/lateFee";

export default function CreateLoanType({ setPageName }) {
  const enums = useEnums();
  const interestModeOptions = buildInterestModeOptions(enums["InterestCalculationMode"], INTEREST_CALCULATION_MODE_OPTIONS);
  const { data: defaultLoanModel } = useGetDefaultLoanModelQuery(undefined, {
    skip: false,
  });
  const [initialLoanModel, setInitialLoanModel] = useState("");

  useEffect(() => {
    if (defaultLoanModel != null && defaultLoanModel !== "" && !initialLoanModel) {
      setInitialLoanModel(defaultLoanModel);
    }
  }, [defaultLoanModel, initialLoanModel]);

  const initialValues = {
    name: "",
    rate: "",
    interest_calculation_mode: "",
    loan_model: initialLoanModel || "",
    min_guarantor: "",
    max_guarantor: "",
    borrowing_multiplier_override: "",
    allow_partial_repayment: "default",
    ...lateFeeInitialValues(null),
    min_loan_amount: "",
    max_loan_amount: "",
    interest_application_week: "",
    interest_application_day: "",
  };
  const validationSchema = Yup.object({
    name: Yup.string().required("Required"),
    rate: Yup.string().required("Required"),
    interest_calculation_mode: Yup.string().required("Required"),
    loan_model: Yup.string().required("Required"),
    min_guarantor: Yup.number()
      .typeError("Required")
      .required("Required")
      .min(0, "Must be 0 or more"),
    max_guarantor: Yup.number()
      .typeError("Required")
      .required("Required")
      .min(0, "Must be 0 or more")
      .test(
        "gte-min",
        "Maximum must be greater than or equal to minimum",
        function (value) {
          const min = Number(this.parent.min_guarantor);
          return value == null || Number.isNaN(min) || Number(value) >= min;
        }
      ),
    borrowing_multiplier_override: Yup.number()
      .nullable()
      .transform((value, original) =>
        original === "" || original == null ? null : Number(original)
      )
      .min(0.01, "Must be greater than 0")
      .max(100, "Must be 100 or less"),
    min_loan_amount: Yup.string().required("Required"),
    max_loan_amount: Yup.string().required("Required"),
    ...lateFeeValidation,
  });

  const [createLoanType, { isLoading }] = useCreateLoanTypeMutation();
  const handleFormSubmit = async (values, { resetForm }) => {
    const payload = {
      name: values.name,
      rate: values.rate,
      interest_calculation_mode: values.interest_calculation_mode,
      loan_model: values.loan_model,
      min_guarantor: Number(values.min_guarantor),
      max_guarantor: Number(values.max_guarantor),
      min_loan_amount: values.min_loan_amount,
      max_loan_amount: values.max_loan_amount,
      borrowing_multiplier_override:
        values.borrowing_multiplier_override === "" ||
        values.borrowing_multiplier_override == null
          ? null
          : Number(values.borrowing_multiplier_override),
      allow_partial_repayment: overrideToPayload(values.allow_partial_repayment),
      ...lateFeeToPayload(values),
    };
    // monthly_target / penalty_* were dropped from company_loan_types — do not send.
    if (values.loan_model === "informal") {
      if (values.interest_application_week)
        payload.interest_application_week = values.interest_application_week;
      if (values.interest_application_day)
        payload.interest_application_day = values.interest_application_day;
    }
    const response = await createLoanType(payload);
    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occured");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occured");
    } else {
      toast.success(data?.message || "Success");
      resetForm();
      setPageName("loan_type");
    }
  };

  return (
    <section className="px-lg-3 min-vh-100 pb-5 mb-3">
      <FilterFind
        back={true}
        backBtn={() => setPageName("loan_type")}
        rightSide={false}
        title="Create New Loan Type"
      />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue }) => {
          const isInformal = values.loan_model === "informal";
          return (
          <Form>
            <CardComp 
              cardBodyClassName={"row py-0 gx-5 gy-4"}
              cardClassName={"border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"}
            >
              <div className="col-12 mb-2">
                <h5 className="fw-bold mb-1 text-black1">
                  Loan Type Information
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  Enter the details for the new loan type
                </p>
              </div>

              <div className="col-12 col-lg-6">
                <TextInputComp
                  LabelText={"Loan Type Name"}
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  placeholder={"e.g., Personal Loan, Business Loan"}
                  name="name"
                  type="text"
                  required
                />
              </div>

              <div className="col-12 col-lg-6">
                <SelectComp2
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  labelText={"Loan Model"}
                  placeholder={"Select loan model"}
                  arrayComp={LOAN_MODEL_OPTIONS}
                  selectedOption={
                    LOAN_MODEL_OPTIONS.find(
                      (opt) => String(opt?.value) === String(values.loan_model)
                    ) ?? null
                  }
                  setSelectedOption={(selectedOption) =>
                    setFieldValue("loan_model", selectedOption?.value ?? "")
                  }
                  errors={
                    touched.loan_model && errors.loan_model ? [errors.loan_model] : []
                  }
                />
              </div>

              <div className="col-12 col-lg-6">
                <TextInputComp
                  LabelText={"Interest Rate (%)"}
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  placeholder={"Enter interest rate (e.g., 12.5)"}
                  name="rate"
                  type="number"
                  required
                />
              </div>

              <div className="col-12 col-lg-6">
                <SelectComp2
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  labelText={"Interest Calculation Mode"}
                  placeholder={"Select calculation mode"}
                  arrayComp={interestModeOptions}
                  selectedOption={
                    interestModeOptions.find(
                      (opt) =>
                        String(opt?.value) ===
                        String(values.interest_calculation_mode)
                    ) ?? null
                  }
                  setSelectedOption={(selectedOption) =>
                    setFieldValue(
                      "interest_calculation_mode",
                      selectedOption?.value ?? ""
                    )
                  }
                  errors={
                    touched.interest_calculation_mode &&
                    errors.interest_calculation_mode
                      ? [errors.interest_calculation_mode]
                      : []
                  }
                />
              </div>

              {isInformal && (
                <>
                  <div className="col-12 mb-3 mt-2">
                    <h6 className="fw-bold mb-1 text-black1">
                      Informal Loan Settings
                    </h6>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                      Interest is applied monthly on the meeting date as a % of current balance
                    </p>
                  </div>
                  <div className="col-12 col-lg-6">
                    <SelectComp2
                      LabelClassName={"fw-bold text-black1 mb-1"}
                      labelText={"Interest application – Week of month"}
                      placeholder={"e.g. First week"}
                      arrayComp={WEEK_OF_MONTH_OPTIONS}
                      selectedOption={
                        WEEK_OF_MONTH_OPTIONS.find(
                          (opt) =>
                            String(opt?.value) ===
                            String(values.interest_application_week)
                        ) ?? null
                      }
                      setSelectedOption={(selectedOption) =>
                        setFieldValue(
                          "interest_application_week",
                          selectedOption?.value ?? ""
                        )
                      }
                    />
                  </div>
                  <div className="col-12 col-lg-6">
                    <SelectComp2
                      LabelClassName={"fw-bold text-black1 mb-1"}
                      labelText={"Interest application – Day of week"}
                      placeholder={"e.g. Monday"}
                      arrayComp={DAY_OF_WEEK_OPTIONS}
                      selectedOption={
                        DAY_OF_WEEK_OPTIONS.find(
                          (opt) =>
                            String(opt?.value) ===
                            String(values.interest_application_day)
                        ) ?? null
                      }
                      setSelectedOption={(selectedOption) =>
                        setFieldValue(
                          "interest_application_day",
                          selectedOption?.value ?? ""
                        )
                      }
                    />
                  </div>
                </>
              )}

              <div className="col-12 col-lg-6"></div>

              <div className="col-12 mb-3 mt-2">
                <h6 className="fw-bold mb-1 text-black1">
                  Guarantor Requirements
                </h6>
                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                  Set 0–0 if this society does not use guarantors for this loan type
                </p>
              </div>

              <div className="col-12 col-lg-6">
                <TextInputComp
                  LabelText={"Minimum Guarantor"}
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  placeholder={"0 or more"}
                  name="min_guarantor"
                  type="number"
                  required
                />
              </div>

              <div className="col-12 col-lg-6">
                <TextInputComp
                  LabelText={"Maximum Guarantor"}
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  placeholder={"0 or more"}
                  name="max_guarantor"
                  type="number"
                  required
                />
              </div>

              <div className="col-12 col-lg-6">
                <TextInputComp
                  LabelText={"Borrowing multiplier override (optional)"}
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  placeholder={"Leave blank to use global setting"}
                  name="borrowing_multiplier_override"
                  type="number"
                  desc="Overrides Loan Application Settings multiplier for this type only"
                />
              </div>

              <div className="col-12 col-lg-6">
                <SelectComp2
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  labelText={"Part payments (optional)"}
                  placeholder={"Use the cooperative setting"}
                  arrayComp={PARTIAL_OVERRIDE_OPTIONS}
                  selectedOption={
                    PARTIAL_OVERRIDE_OPTIONS.find(
                      (opt) => opt.value === values.allow_partial_repayment
                    ) ?? PARTIAL_OVERRIDE_OPTIONS[0]
                  }
                  setSelectedOption={(selectedOption) =>
                    setFieldValue(
                      "allow_partial_repayment",
                      selectedOption?.value ?? "default"
                    )
                  }
                />
                <small className="text-muted">
                  Lets members pay any amount towards an installment for this loan type only
                </small>
              </div>

              <LateFeeFields values={values} setFieldValue={setFieldValue} />

              <div className="col-12 mb-3 mt-2">
                <h6 className="fw-bold mb-1 text-black1">
                  Loan Amount Range
                </h6>
                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                  Define the minimum and maximum loan amounts allowed
                </p>
              </div>

              <div className="col-12 col-lg-6">
                <MoneyInputComp
                  LabelText={"Minimum Loan Amount"}
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  placeholder={"Enter minimum loan amount"}
                  desc="The minimum amount that can be borrowed"
                  name={"min_loan_amount"}
                />
              </div>

              <div className="col-12 col-lg-6">
                <MoneyInputComp
                  LabelText={"Maximum Loan Amount"}
                  LabelClassName={"fw-bold text-black1 mb-1"}
                  placeholder={"Enter maximum loan amount"}
                  desc="The maximum amount that can be borrowed"
                  name={"max_loan_amount"}
                />
              </div>
            </CardComp>

            <div className="d-flex justify-content-end gap-3 mt-4">
              <ButtonComp
                type="submit"
                btnText={"Create Loan Type"}
                btnClassName={
                  "bg-primary1 py-2 px-5 fw-bold text-white border-0 rounded"
                }
                style={{ minWidth: "200px" }}
                loading={isLoading}
              />
            </div>
          </Form>
          );
        }}
      </Formik>
    </section>
  );
}
